import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { AnnouncementLevel, AnnouncementPattern } from '@citizen-walk/shared-types';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import {
  archiveAnnouncement,
  createAnnouncement,
  deleteAnnouncement,
  useAnnouncements,
  type AnnouncementListItem,
} from './useAnnouncements';

/**
 * お知らせ管理画面 (admin 第 15 弾、Phase 5 REQ-4.1.25 / 4.3.33)。
 *
 * 旧 `old/apps/admin/src/pages/AnnouncementsPage.tsx` の form + 履歴一覧を v2 callable 不要・
 * Firestore 直接 CRUD で踏襲。
 *
 * 旧との差分:
 *   - 表示時間 (分) 指定 → endAt 直接指定 (datetime-local) に変更 (旧は durationMin → endAt 計算、
 *     v2 では admin が明示的に期間を見ながら設定できる input にする)
 *   - level バッジ色を shared-ui Tailwind preset に統一 (info=neutral / warning=amber / critical=red)
 *   - `forceReshow` toggle を form に追加 (REQ-4.3.33、旧は未提供)
 *   - 物理 delete ボタンを追加 (archive とは別、誤投稿の取消用)
 *
 * 認可: master / district admin (担当地区) のみ操作可。rules で deny される (UI でも guard)。
 */

const LEVEL_LABEL: Record<AnnouncementLevel, string> = {
  info: '情報',
  warning: '注意',
  critical: '重要',
};

const LEVEL_BADGE: Record<AnnouncementLevel, string> = {
  info: 'bg-slate-300 text-slate-800',
  warning: 'bg-warning-600 text-white',
  critical: 'bg-danger-700 text-white',
};

const PATTERN_LABEL: Record<AnnouncementPattern, string> = {
  banner: 'バナー',
  modal: 'モーダル',
};

function isActive(item: AnnouncementListItem, now = new Date()): boolean {
  if (item.archivedAt) return false;
  if (item.startAt && item.startAt.getTime() > now.getTime()) return false;
  if (item.endAt && item.endAt.getTime() < now.getTime()) return false;
  return true;
}

/** datetime-local input ↔ ISO8601 変換 (local tz を尊重した変換)。 */
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** ISO8601 → datetime-local input value (local tz)。 */
function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AnnouncementsAdminPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const announcementsState = useAnnouncements(districtId ?? null);
  const sessionsState = useSessions(districtId ?? null);
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState<AnnouncementLevel>('info');
  const [pattern, setPattern] = useState<AnnouncementPattern>('banner');
  // startAt: 未入力で即時、endAt: 未入力で無期限
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return isoToLocal(now.toISOString());
  });
  const [forceReshow, setForceReshow] = useState(false);
  // targetSessionIds: 空 Set = 「全セッション」配信 (旧 REQ-4.3.33 default、user 側 filter は targetSessionIds.length === 0 で全員許可)
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const uid = authState.user?.uid ?? '';

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!districtId || !canManage) return;
    if (!title.trim() || !body.trim()) {
      toast('error', 'タイトルと本文を入力してください');
      return;
    }
    setSubmitting(true);
    try {
      await createAnnouncement(
        districtId,
        {
          title,
          body,
          level,
          pattern,
          startAt: localToIso(startAt),
          endAt: localToIso(endAt),
          forceReshow,
          targetSessionIds: [...selectedSessionIds],
        },
        uid,
      );
      toast('success', 'お知らせを送信しました');
      setTitle('');
      setBody('');
      setForceReshow(false);
      setSelectedSessionIds(new Set());
    } catch (e) {
      toast('error', `送信失敗: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive(item: AnnouncementListItem) {
    if (!districtId || !canManage) return;
    if (!confirm(`「${item.title || '(無題)'}」を終了します。よろしいですか？`)) return;
    setBusyId(item.id);
    try {
      await archiveAnnouncement(districtId, item.id, uid);
      toast('success', '配信を終了しました');
    } catch (e) {
      toast('error', `終了失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(item: AnnouncementListItem) {
    if (!districtId || !canManage) return;
    if (
      !confirm(`「${item.title || '(無題)'}」を完全に削除します。履歴も消えます。よろしいですか？`)
    ) {
      return;
    }
    setBusyId(item.id);
    try {
      await deleteAnnouncement(districtId, item.id);
      toast('success', 'お知らせを削除しました');
    } catch (e) {
      toast('error', `削除失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  const items = announcementsState.status === 'ready' ? announcementsState.items : [];

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          お知らせ管理
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.1.25 / 4.3.33 — 地区別の banner / modal お知らせ配信。
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区のお知らせ管理権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      <Card padding="lg" className="mb-6">
        <h2 className="text-base font-bold text-slate-900 mb-3">新規お知らせ</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">タイトル *</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={60}
              required
              disabled={!canManage || submitting}
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">本文 *</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={500}
              rows={4}
              required
              disabled={!canManage || submitting}
              className="px-3 py-2 border border-slate-300 rounded text-sm"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">レベル</span>
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value as AnnouncementLevel)}
                disabled={!canManage || submitting}
                className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
              >
                <option value="info">情報</option>
                <option value="warning">注意</option>
                <option value="critical">重要</option>
              </select>
            </label>
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">表示形式</span>
              <select
                value={pattern}
                onChange={(e) => setPattern(e.target.value as AnnouncementPattern)}
                disabled={!canManage || submitting}
                className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
              >
                <option value="banner">バナー (上部 slim bar、複数同時可)</option>
                <option value="modal">モーダル (中央ダイアログ、確認必須)</option>
              </select>
            </label>
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">配信開始 (空欄 = 即時)</span>
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                disabled={!canManage || submitting}
                className="min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">配信終了 (空欄 = 無期限)</span>
              <input
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                disabled={!canManage || submitting}
                className="min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">
              対象セッション (未選択 = 全セッション配信)
            </span>
            {sessionsState.status === 'loading' && (
              <p className="text-xs text-slate-400">セッション読込中…</p>
            )}
            {sessionsState.status === 'error' && (
              <p className="text-xs text-danger-700">
                セッション読込エラー: {sessionsState.error.message}
              </p>
            )}
            {sessionsState.status === 'ready' &&
              sessionsState.items.filter((s) => !s.archivedAt).length === 0 && (
                <p className="text-xs text-slate-400">
                  アクティブなセッションがありません — 全セッション配信されます。
                </p>
              )}
            {sessionsState.status === 'ready' &&
              sessionsState.items.filter((s) => !s.archivedAt).length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded">
                  {sessionsState.items
                    .filter((s) => !s.archivedAt)
                    .map((s) => {
                      const checked = selectedSessionIds.has(s.id);
                      return (
                        <label
                          key={s.id}
                          className={`text-xs px-3 py-2 rounded cursor-pointer border min-h-touch inline-flex items-center ${
                            checked
                              ? 'bg-primary-100 border-primary-400 text-primary-800'
                              : 'bg-white border-slate-300 text-slate-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canManage || submitting}
                            onChange={(e) => {
                              setSelectedSessionIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(s.id);
                                else next.delete(s.id);
                                return next;
                              });
                            }}
                            className="mr-1 align-middle"
                          />
                          {s.name} <span className="text-[10px] text-slate-400">({s.code})</span>
                        </label>
                      );
                    })}
                </div>
              )}
            <p className="text-[11px] text-slate-400">
              選択中:{' '}
              {selectedSessionIds.size === 0
                ? '全セッション'
                : `${selectedSessionIds.size} セッション`}
            </p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={forceReshow}
              onChange={(e) => setForceReshow(e.target.checked)}
              disabled={!canManage || submitting}
              className="mt-1"
            />
            <span>
              <strong>forceReshow</strong> — 既読 user にも再表示する (緊急訂正・差替時に使用)
            </span>
          </label>
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={!canManage || submitting}>
              {submitting ? '送信中…' : '📢 送信'}
            </Button>
          </div>
        </form>
      </Card>

      <h2 className="text-base font-bold text-slate-900 mb-3">送信履歴</h2>

      {announcementsState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {announcementsState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {announcementsState.error.message}</p>
        </Card>
      )}

      {announcementsState.status === 'ready' && items.length === 0 && (
        <Card padding="md">
          <p className="text-sm text-slate-600">まだお知らせはありません。</p>
        </Card>
      )}

      {announcementsState.status === 'ready' && items.length > 0 && (
        <ul className="grid grid-cols-1 gap-2">
          {items.map((a) => {
            const active = isActive(a);
            return (
              <li key={a.id}>
                <Card padding="md" className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${LEVEL_BADGE[a.level]}`}>
                        {LEVEL_LABEL[a.level]}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                        {PATTERN_LABEL[a.pattern]}
                      </span>
                      {active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-success-600 text-white">
                          配信中
                        </span>
                      )}
                      {a.archivedAt && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-300 text-slate-700">
                          終了済
                        </span>
                      )}
                      {a.forceReshow && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700"
                          title="forceReshow: 既読 user にも再表示"
                        >
                          🔁 forceReshow
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-bold text-slate-900 truncate">
                      {a.title || '(無題)'}
                    </p>
                    <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                      {a.body}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      {a.startAt ? a.startAt.toLocaleString('ja-JP') : '即時'} 〜{' '}
                      {a.endAt ? a.endAt.toLocaleString('ja-JP') : '無期限'}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      対象:{' '}
                      {a.targetSessionIds.length === 0
                        ? '全セッション'
                        : sessionsState.status !== 'ready'
                          ? `${a.targetSessionIds.length} セッション (読込中…)`
                          : a.targetSessionIds
                              .map((id) => {
                                const s = sessionsState.items.find((x) => x.id === id);
                                return s ? `${s.name} (${s.code})` : `(削除済) ${id}`;
                              })
                              .join(' / ')}
                    </p>
                  </div>
                  {canManage && (
                    <div className="shrink-0 flex flex-col gap-1">
                      {active && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void handleArchive(a)}
                          disabled={busyId === a.id}
                        >
                          終了
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => void handleDelete(a)}
                        disabled={busyId === a.id}
                      >
                        削除
                      </Button>
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
